import { useMachine } from '@xstate/react'
import Modal from 'react-modal'
import { Box, Button, Close, Flex } from 'theme-ui'
import { alertMachine } from 'machines'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Loading } from 'components'
import { alertMachineContext, alertMachineEvents } from 'machines/alertMachine'
import { State } from 'xstate'
import { ErrorMessage } from 'components/Form'

const modalStyle = {
  content: {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    padding: 0,
    zIndex: 99999,
    background: 'none',
    border: 'none',
    inset: 0,
    display: 'flex',
    borderRadius: 0,
    margin: 'auto',
  },
  overlay: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
}

const AlertModalContext = createContext(
  {} as {
    state: State<
      alertMachineContext,
      alertMachineEvents,
      any,
      { value: any; context: alertMachineContext }
    >
    createAlert: (alert: {
      content: string | JSX.Element
      confirmText: string | JSX.Element
    }) => void
    interceptConfirm: (preConfirm: () => void) => void
    finalizeConfirm: () => void
    abortConfirm: (error: string) => void
    handleCancel: () => void
    interceptCancel: (preCancel: () => void) => void
    finalizeCancel: () => void
    setDisabled: (value: boolean) => void
  }
)

export const ConfirmationModalProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [state, send] = useMachine(alertMachine)
  const [error, setError] = useState<string>('')
  const [disabled, setDisabled] = useState<boolean>(false)
  const showing = state.matches('showing') || state.matches('processing')

  const createAlert = ({
    content,
    confirmText,
  }: {
    content: string | JSX.Element
    confirmText: string | JSX.Element
  }) => {
    setDisabled(false)
    send('CREATE', { content, confirmText })
  }

  const finalizeCancel = useCallback(() => {
    setDisabled(false)
    send('CANCEL')
  }, [send])

  const finalizeConfirm = useCallback(() => {
    setDisabled(false)
    send('CONFIRM')
  }, [send])

  const [preConfirm, setConfirm] = useState(() => finalizeConfirm)
  const [preCancel, setCancel] = useState(() => finalizeCancel)

  const handleConfirm = useCallback(() => {
    setDisabled(true)
    send('PROCESSING')
    preConfirm()
  }, [send, preConfirm])

  const handleCancel = useCallback(() => {
    setDisabled(true)
    preCancel()
  }, [preCancel])

  const abortConfirm = useCallback(
    (error: string) => {
      setDisabled(false)
      setError(error)
      send('ABORT_CONFIRM')
    },
    [send, setError]
  )

  const interceptConfirm = useCallback(
    (preConfirm: () => void) => {
      setConfirm(() => preConfirm)
    },
    [setConfirm]
  )
  const interceptCancel = useCallback(
    (preCancel: () => void) => {
      setCancel(() => preCancel)
    },
    [setCancel]
  )
  //cleanUp
  useEffect(() => {
    if (state.matches('confirmed') || state.matches('cancelled')) send('RESET')
  }, [state, state.value, send])

  return (
    <AlertModalContext.Provider
      value={{
        state,
        createAlert,
        interceptConfirm,
        finalizeConfirm,
        handleCancel,
        interceptCancel,
        finalizeCancel,
        abortConfirm,
        setDisabled,
      }}>
      {showing && (
        <Modal isOpen style={modalStyle} ariaHideApp={false}>
          <Box
            sx={{
              ml: 0,
              mt: 0,
              width: '100vw',
              height: '100vh',
              overflow: 'auto',
            }}>
            <Box
              data-cy="confirmation-modal"
              sx={{
                position: 'relative',
                p: [20, 45],
                backgroundColor: 'white',
                borderRadius: 7,
                margin: 'auto',
                top: '30vh',
                width: [343, 660],
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  right: 3,
                  top: 3,
                  width: 30,
                  height: 30,
                  cursor: 'pointer',
                }}
                onClick={handleCancel}>
                <Close />
              </Box>

              {state.context.content}

              <ErrorMessage>{error && <span>{error}</span>}</ErrorMessage>
              <Flex variant="forms.buttons.container">
                <Button
                  disabled={disabled}
                  type="submit"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: [150, 200],
                    minHeight: 50,
                  }}
                  data-cy="confirmation-modal-confirm"
                  onClick={handleConfirm}>
                  {state.matches('showing') ? (
                    state.context.confirmText
                  ) : (
                    <Loading />
                  )}
                </Button>
                <Button
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bg: 'white',
                    minHeight: 50,
                    minWidth: [120, 100],
                  }}
                  data-cy="confirmation-modal-cancel"
                  type="button"
                  onClick={handleCancel}>
                  Cancel
                </Button>
              </Flex>
            </Box>
          </Box>
        </Modal>
      )}

      {children}
    </AlertModalContext.Provider>
  )
}

export const useAlert = () => {
  const {
    createAlert,
    state,
    interceptConfirm,
    finalizeConfirm,
    abortConfirm,
    handleCancel,
    interceptCancel,
    finalizeCancel,
    setDisabled,
  } = useContext(AlertModalContext)
  return {
    createAlert,
    state,
    interceptConfirm,
    finalizeConfirm,
    abortConfirm,
    handleCancel,
    interceptCancel,
    finalizeCancel,
    setDisabled,
  }
}
